import React, { useState, useEffect } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { toast } from 'sonner'
import { Search, Printer, Package, History } from 'lucide-react'
import { LabelProduct, PrintResponse, PrintJob } from '../types/barcode'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'

export default function LabelPrintPage() {
    const { accessToken } = useAuthStore()
    const [searchQuery, setSearchQuery] = useState('')
    const [products, setProducts] = useState<LabelProduct[]>([])
    const [loading, setLoading] = useState(false)
    const [printing, setPrinting] = useState<string | null>(null)
    const [customQty, setCustomQty] = useState<Record<string, number>>({})
    const [showHistory, setShowHistory] = useState(false)
    const [printHistory, setPrintHistory] = useState<PrintJob[]>([])
    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
    })

    // Ürünleri yükle
    const loadProducts = async (page = 1) => {
        setLoading(true)
        try {
            console.log('🔑 Access Token (Labels):', accessToken ? 'Token mevcut' : 'Token yok')
            const params = new URLSearchParams({
                page: page.toString(),
                size: '20'
            })

            if (searchQuery.trim()) {
                params.append('q', searchQuery.trim())
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/labels/products?${params}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (response.ok) {
                const data = await response.json()
                setProducts(data.products)
                setPagination(data.pagination)
            } else {
                toast.error('Ürün yükleme hatası')
            }
        } catch (error) {
            toast.error('Ürün yükleme hatası')
        } finally {
            setLoading(false)
        }
    }

    // Sayfa yüklendiğinde ürünleri getir
    useEffect(() => {
        loadProducts()
    }, [])

    // Arama
    const handleSearch = () => {
        loadProducts(1)
    }

    // Enter ile arama
    const handleSearchKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    // Etiket yazdır
    const printLabel = async (product: LabelProduct, mode: 'stock' | 'custom') => {
        const qty = mode === 'stock' ? product.stock : (customQty[product.id] || 1)

        if (qty <= 0) {
            toast.error('Geçerli bir adet giriniz')
            return
        }

        setPrinting(product.id)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/labels/print`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    productId: product.id,
                    qty,
                    mode
                })
            })

            if (response.ok) {
                const result: PrintResponse = await response.json()
                toast.success(`${qty} adet etiket hazırlandı`)

                // Yazdırma seçenekleri göster
                showPrintOptions(result)
            } else {
                const error = await response.json()
                toast.error(error.error || 'Yazdırma hatası')
            }
        } catch (error) {
            toast.error('Yazdırma hatası')
        } finally {
            setPrinting(null)
        }
    }

    // Yazdırma seçenekleri modal
    const showPrintOptions = (result: PrintResponse) => {
        const modal = document.createElement('div')
        modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'

        modal.innerHTML = `
            <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 class="text-lg font-semibold mb-4">Etiket Yazdırma Seçenekleri</h3>
                <div class="space-y-3">
                    <p class="text-sm text-gray-600">
                        ${result.product.name} - ${result.printJob.qty} adet
                    </p>
                    <div class="border rounded p-2 text-center">
                        <img src="${result.formats.png}" alt="Barkod" class="mx-auto mb-2" style="max-width: 200px;">
                        <p class="text-sm font-medium mt-2">${result.product.barcode}</p>
                    </div>
                    <div class="flex space-x-2">
                        <button id="print-pdf" class="flex-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                            PDF İndir
                        </button>
                        <button id="print-zpl" class="flex-1 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600">
                            ZPL İndir
                        </button>
                        <button id="print-direct" class="flex-1 bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600">
                            Doğrudan Yazdır
                        </button>
                    </div>
                    <button id="close-modal" class="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
                        Kapat
                    </button>
                </div>
            </div>
        `

        document.body.appendChild(modal)

        // Event listeners
        modal.querySelector('#print-pdf')?.addEventListener('click', () => {
            // Temiz HTML template - sadece barkod ve kod
            const cleanHtml = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>Barkod - ${result.product.barcode}</title>
                    <style>
                            body {
                                margin: 0;
                                padding: 20px;
                                font-family: Arial, sans-serif;
                                background: white;
                            }
                            .barcode-container {
                                text-align: center;
                                padding: 20px;
                            }
                            .barcode-image {
                                max-width: 100%;
                                height: auto;
                                margin-bottom: 15px;
                            }
                            .barcode-code {
                                font-size: 18px;
                                font-weight: bold;
                                color: #000;
                                margin: 0;
                            }
                            @media print {
                                body { 
                                    margin: 0 !important; 
                                    padding: 10px !important; 
                                }
                                .barcode-container { 
                                    padding: 10px !important; 
                                }
                                /* Browser header ve footer'ı tamamen gizle */
                                @page {
                                    margin: 0 !important;
                                    size: auto !important;
                                }
                                /* Tüm browser ek bilgilerini gizle */
                                * {
                                    -webkit-print-color-adjust: exact !important;
                                    color-adjust: exact !important;
                                }
                                /* Header ve footer gizleme */
                                html, body {
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                }
                                /* Tüm browser header/footer bilgilerini gizle */
                                @page :first {
                                    margin: 0 !important;
                                }
                                @page :left {
                                    margin: 0 !important;
                                }
                                @page :right {
                                    margin: 0 !important;
                                }
                                /* Browser'ın kendi eklediği tüm bilgileri gizle */
                                @page {
                                    margin: 0 !important;
                                    padding: 0 !important;
                                }
                            }
                            /* Print preview'da da gizle */
                            @media screen {
                                body::before,
                                body::after {
                                    display: none !important;
                                }
                            }
                            /* Tüm browser ek bilgilerini gizle */
                            html {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            /* Browser'ın kendi eklediği bilgileri gizle */
                            body {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            /* Tüm browser header/footer bilgilerini gizle */
                            @media print {
                                @page {
                                    margin: 0 !important;
                                    padding: 0 !important;
                                }
                                body {
                                    margin: 0 !important;
                                    padding: 10px !important;
                                }
                            }
                        </style>
                </head>
                <body>
                    <div class="barcode-container">
                        <img src="${result.formats.png}" alt="Barkod" class="barcode-image">
                        <p class="barcode-code">${result.product.barcode}</p>
                    </div>
                </body>
                </html>
            `
            downloadFile(cleanHtml, `barkod-${result.product.barcode}.html`, 'text/html')
            document.body.removeChild(modal)
        })

        modal.querySelector('#print-zpl')?.addEventListener('click', () => {
            downloadFile(result.formats.zpl, `etiket-${result.product.sku}.zpl`, 'text/plain')
            document.body.removeChild(modal)
        })

        modal.querySelector('#print-direct')?.addEventListener('click', () => {
            const printWindow = window.open('', '_blank')
            if (printWindow) {
                // Temiz HTML template - sadece barkod ve kod
                const cleanHtml = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <title>Barkod - ${result.product.barcode}</title>
                        <style>
                            body {
                                margin: 0;
                                padding: 20px;
                                font-family: Arial, sans-serif;
                                background: white;
                            }
                            .barcode-container {
                                text-align: center;
                                padding: 20px;
                            }
                            .barcode-image {
                                max-width: 100%;
                                height: auto;
                                margin-bottom: 15px;
                            }
                            .barcode-code {
                                font-size: 18px;
                                font-weight: bold;
                                color: #000;
                                margin: 0;
                            }
                            @media print {
                                body { 
                                    margin: 0 !important; 
                                    padding: 10px !important; 
                                }
                                .barcode-container { 
                                    padding: 10px !important; 
                                }
                                /* Browser header ve footer'ı tamamen gizle */
                                @page {
                                    margin: 0 !important;
                                    size: auto !important;
                                }
                                /* Tüm browser ek bilgilerini gizle */
                                * {
                                    -webkit-print-color-adjust: exact !important;
                                    color-adjust: exact !important;
                                }
                                /* Header ve footer gizleme */
                                html, body {
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                }
                                /* Tüm browser header/footer bilgilerini gizle */
                                @page :first {
                                    margin: 0 !important;
                                }
                                @page :left {
                                    margin: 0 !important;
                                }
                                @page :right {
                                    margin: 0 !important;
                                }
                                /* Browser'ın kendi eklediği tüm bilgileri gizle */
                                @page {
                                    margin: 0 !important;
                                    padding: 0 !important;
                                }
                            }
                            /* Print preview'da da gizle */
                            @media screen {
                                body::before,
                                body::after {
                                    display: none !important;
                                }
                            }
                            /* Tüm browser ek bilgilerini gizle */
                            html {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            /* Browser'ın kendi eklediği bilgileri gizle */
                            body {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            /* Tüm browser header/footer bilgilerini gizle */
                            @media print {
                                @page {
                                    margin: 0 !important;
                                    padding: 0 !important;
                                }
                                body {
                                    margin: 0 !important;
                                    padding: 10px !important;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="barcode-container">
                            <img src="${result.formats.png}" alt="Barkod" class="barcode-image">
                            <p class="barcode-code">${result.product.barcode}</p>
                        </div>
                    </body>
                    </html>
                `
                printWindow.document.write(cleanHtml)
                printWindow.document.close()

                // Browser header ve footer'ı gizle
                printWindow.document.body.style.margin = '0'
                printWindow.document.body.style.padding = '10px'

                // Tüm browser header/footer bilgilerini gizle
                printWindow.document.documentElement.style.margin = '0'
                printWindow.document.documentElement.style.padding = '0'

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Browser'ın kendi eklediği tüm bilgileri gizle
                printWindow.document.documentElement.style.setProperty('-webkit-print-color-adjust', 'exact', 'important')
                printWindow.document.documentElement.style.setProperty('print-color-adjust', 'exact', 'important')

                // Print öncesi CSS'i güçlendir
                const style = printWindow.document.createElement('style')
                style.textContent = `
                    @page { 
                        margin: 0 !important; 
                        size: auto !important; 
                    }
                    @page :first { margin: 0 !important; }
                    @page :left { margin: 0 !important; }
                    @page :right { margin: 0 !important; }
                    body { 
                        margin: 0 !important; 
                        padding: 10px !important; 
                    }
                    * { 
                        -webkit-print-color-adjust: exact !important; 
                        color-adjust: exact !important; 
                    }
                    html, body {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    /* Tüm browser header/footer bilgilerini gizle */
                    @media print {
                        @page {
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        body {
                            margin: 0 !important;
                            padding: 10px !important;
                        }
                    }
                    /* Browser'ın kendi eklediği tüm bilgileri gizle */
                    @media print {
                        @page {
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        body {
                            margin: 0 !important;
                            padding: 10px !important;
                        }
                    }
                `
                printWindow.document.head.appendChild(style)

                printWindow.focus()
                printWindow.print()
            }
            document.body.removeChild(modal)
        })

        modal.querySelector('#close-modal')?.addEventListener('click', () => {
            document.body.removeChild(modal)
        })

        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                document.body.removeChild(modal)
            }
        })
    }

    // Dosya indir
    const downloadFile = (content: string, filename: string, mimeType: string) => {
        const blob = new Blob([content], { type: mimeType })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    // Custom adet değiştir
    const handleCustomQtyChange = (productId: string, qty: number) => {
        setCustomQty(prev => ({
            ...prev,
            [productId]: Math.max(1, qty)
        }))
    }

    // Geçmişi yükle
    const loadHistory = async () => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/labels/history`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (response.ok) {
                const data = await response.json()
                setPrintHistory(data.printJobs)
            } else {
                toast.error('Geçmiş yüklenemedi')
            }
        } catch (error) {
            toast.error('Geçmiş yüklenemedi')
        }
    }

    // Geçmişi göster/gizle
    const toggleHistory = () => {
        setShowHistory(!showHistory)
        if (!showHistory) {
            loadHistory()
        }
    }

    return (
        <div className="container mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                <div className="flex items-center space-x-2">
                    <Printer className="h-5 w-5 sm:h-6 sm:w-6" />
                    <h1 className="text-xl sm:text-2xl font-bold">Barkod Etiket Yazdırma</h1>
                </div>
                <Button variant="outline" onClick={toggleHistory} className="w-full sm:w-auto">
                    <History className="w-4 h-4 mr-2" />
                    {showHistory ? 'Ürünleri Göster' : 'Geçmişi Göster'}
                </Button>
            </div>

            {!showHistory ? (
                <>
                    {/* Arama */}
                    <Card>
                        <CardHeader className="pb-3 sm:pb-4">
                            <CardTitle className="text-lg sm:text-xl">Ürün Ara</CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 sm:px-6">
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <Input
                                    placeholder="SKU, ürün adı veya barkod ile ara..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={handleSearchKeyPress}
                                    className="w-full"
                                />
                                <Button onClick={handleSearch} disabled={loading} className="w-full sm:w-auto">
                                    <Search className="w-4 h-4 mr-2" />
                                    {loading ? 'Arıyor...' : 'Ara'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Ürün Listesi */}
                    <Card>
                        <CardHeader className="pb-3 sm:pb-4">
                            <CardTitle className="text-lg sm:text-xl">Ürünler</CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 sm:px-6">
                            {loading ? (
                                <div className="text-center py-6 sm:py-8">
                                    <div className="text-sm sm:text-base">Yükleniyor...</div>
                                </div>
                            ) : products.length === 0 ? (
                                <div className="text-center py-6 sm:py-8 text-gray-500">
                                    <div className="text-sm sm:text-base">Ürün bulunamadı</div>
                                </div>
                            ) : (
                                <div className="space-y-3 sm:space-y-4">
                                    {products.map((product) => (
                                        <div key={product.id} className="border rounded-lg p-3 sm:p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                                {/* Ürün Fotoğrafı */}
                                                <div className="flex-shrink-0">
                                                    {product.image1 ? (
                                                        <img
                                                            src={product.image1}
                                                            alt={product.name}
                                                            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded"
                                                        />
                                                    ) : (
                                                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded flex items-center justify-center">
                                                            <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Ürün Bilgileri */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-sm sm:text-base truncate">{product.name}</h3>
                                                    <p className="text-xs sm:text-sm text-gray-500">SKU: {product.sku}</p>
                                                    <p className="text-xs sm:text-sm text-gray-500">
                                                        Barkod: <span className="font-medium">{product.barcode || 'Otomatik oluşturulacak'}</span>
                                                    </p>
                                                    <div className="flex items-center space-x-2 mt-1">
                                                        <Badge variant="secondary" className="text-xs">
                                                            Stok: {product.stock}
                                                        </Badge>
                                                    </div>
                                                </div>

                                                {/* Yazdırma Seçenekleri */}
                                                <div className="flex flex-col space-y-2 w-full sm:w-auto">
                                                    {/* Stok Adedi */}
                                                    <Button
                                                        size="sm"
                                                        onClick={() => printLabel(product, 'stock')}
                                                        disabled={printing === product.id || product.stock === 0}
                                                        className="w-full sm:w-auto text-xs sm:text-sm"
                                                    >
                                                        {printing === product.id ? 'Yazdırılıyor...' : `${product.stock} Adet Yazdır`}
                                                    </Button>

                                                    {/* Custom Adet */}
                                                    <div className="flex space-x-1">
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            value={customQty[product.id] || 1}
                                                            onChange={(e) => handleCustomQtyChange(product.id, parseInt(e.target.value) || 1)}
                                                            className="w-16 sm:w-20 h-8 text-xs sm:text-sm"
                                                        />
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => printLabel(product, 'custom')}
                                                            disabled={printing === product.id}
                                                            className="flex-1 sm:flex-none text-xs sm:text-sm"
                                                        >
                                                            Yazdır
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Sayfalama */}
                            {pagination.totalPages > 1 && (
                                <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-3 mt-4 sm:mt-6">
                                    <Button
                                        variant="outline"
                                        onClick={() => loadProducts(pagination.page - 1)}
                                        disabled={!pagination.hasPrev || loading}
                                        className="w-full sm:w-auto text-xs sm:text-sm"
                                    >
                                        Önceki
                                    </Button>
                                    <span className="flex items-center px-3 text-xs sm:text-sm">
                                        {pagination.page} / {pagination.totalPages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        onClick={() => loadProducts(pagination.page + 1)}
                                        disabled={!pagination.hasNext || loading}
                                        className="w-full sm:w-auto text-xs sm:text-sm"
                                    >
                                        Sonraki
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            ) : (
                /* Yazdırma Geçmişi */
                <Card>
                    <CardHeader className="pb-3 sm:pb-4">
                        <CardTitle className="text-lg sm:text-xl">Yazdırma Geçmişi</CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 sm:px-6">
                        {printHistory.length === 0 ? (
                            <div className="text-center py-6 sm:py-8 text-gray-500">
                                <div className="text-sm sm:text-base">Yazdırma geçmişi bulunamadı</div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {printHistory.map((job) => (
                                    <div key={job.id} className="border rounded p-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm sm:text-base truncate">{job.product?.name || job.sku}</p>
                                            <p className="text-xs sm:text-sm text-gray-500">
                                                {job.qty} adet • {job.mode === 'stock' ? 'Stok Adedi' : 'Özel Adet'}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {new Date(job.createdAt).toLocaleString('tr-TR')}
                                            </p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:text-right gap-2 sm:gap-0">
                                            <Badge className={
                                                job.status === 'completed' ? 'bg-green-500' :
                                                    job.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                                            }>
                                                {job.status === 'completed' ? 'Tamamlandı' :
                                                    job.status === 'failed' ? 'Başarısız' : 'Bekliyor'}
                                            </Badge>
                                            {job.user && (
                                                <p className="text-xs text-gray-500">
                                                    {job.user.firstName} {job.user.lastName}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    )
}
